import React, { useEffect, useState } from "react"
import { FormattedDate, FormattedMessage, FormattedNumber } from "react-intl"
import { Link } from "react-router-dom"
import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import SortLink from "../common/listing/sortlink.component.jsx"
import InvoiceStatusBadge from "../invoice/status.component.jsx"
import QuoteStatusBadge from "../quote/status.component.jsx"
import CreditMemoStatusBadge from "../invoice/creditmemostatus.component.jsx"
import CreditMemoModalLink from "../invoice/creditmemomodallink.component"
import StandardList from "../common/listing/list.component.jsx"
import NoResult from "../common/listing/noresult.component.jsx"
import Pagination from "../common/listing/pagination.component.jsx"
import LoadingSpinner from "../common/spinner/loading.component.jsx"
import SearchService from "../state/search/SearchService"
import { isError } from "../state/helpers/Error"
import InvoiceExportModal from "../invoice/exportmodal.component"
import Icon from "../common/icon/icon.component"
import { INVOICE, INVOICES } from "../state/invoice/Constants"
import { QUOTE, QUOTES } from "../state/quote/Constants"
import { CREDIT_MEMO, CREDIT_MEMOS } from "../state/credit-memo/Constants"
import { SORT_BY_FIELD } from "../utils"

type OwnProps = {
    priceListObjectType: string,
    priceListId: string,
    onChange?: () => () => void,
}

const PriceListOrderListingComponent = (props: OwnProps) => {

    const [searchResult, setSearchResult] = useState({
        headers: [] as JSX.Element[],
        rows: [],
        totalItemsCount: 0,
    })
    const [searchFilters, setSearchFilters] = useState({
        pageSize: 10,
        activePage: 1,
        sortField: SORT_BY_FIELD.IDENTIFIER,
        sortAsc: false,
    })
    const [loading, setLoading] = useState<boolean>(true)
    const [exportModal, setExportModal] = useState(<></>)
    const [data, setData] = useState({})

    useEffect(() => {
        refreshListing()
    }, [searchFilters])

    useEffect(() => {
        if (props.onChange) {
            props.onChange()
        }
    }, [searchResult])

    const refreshListing = () => {
        setLoading(true)

        const postData = {
            keywords: "*",
            objects: [getSearchObjectType(props.priceListObjectType)],
            fields: [
                "identifier",
            ],
            pageSize: searchFilters.pageSize,
            activePage: searchFilters.activePage,
            sortByField: searchFilters.sortField,
            sortAsc: searchFilters.sortAsc,
            filters: {
                "{oneOf}": {
                    priceListId: props.priceListId,
                    priceListReferenceId: props.priceListId,
                },
            },
            lightResult: true,
        }
        setData(postData)

        SearchService.pricelistListingSearch(postData).then(res => {
            if (!isError(res)) {
                setSearchResult({
                    headers: headers(),
                    // @ts-ignore todo fix
                    rows: res.data.map(q => row(q)),
                    totalItemsCount: res.metadata.totalItemsCount,
                })
            }
            setLoading(false)
        })

    }

    const getSearchObjectType = (objectType: string): string => {
        switch (objectType) {
        case INVOICE: return INVOICES
        case QUOTE: return QUOTES
        case CREDIT_MEMO: return CREDIT_MEMOS
        default: return ""
        }
    }

    const getOrderDateSortField = () => {
        switch (props.priceListObjectType) {
        case INVOICE: return "sentAt"
        case QUOTE: return "proposedAt"
        default: return "createdAt"
        }
    }

    const headers = () => {
        const headers = [
            <SortLink
                key={1}
                field={SORT_BY_FIELD.IDENTIFIER}
                sortField={searchFilters.sortField}
                asc={searchFilters.sortAsc}
                onChange={handleSortLinkChange}
            >
                <FormattedMessage
                    id="price_list.quotes.listing.headers.id_label"
                    defaultMessage="ID"
                />
            </SortLink>,
            <SortLink
                key={2}
                field={"contact.nameSort"}
                sortField={searchFilters.sortField}
                asc={searchFilters.sortAsc}
                onChange={handleSortLinkChange}
            >
                <FormattedMessage
                    id="price_list.quotes.listing.headers.contact_label"
                    defaultMessage="Contact"
                />
            </SortLink>,
            <SortLink
                key={3}
                field={getOrderDateSortField()}
                sortField={searchFilters.sortField}
                asc={searchFilters.sortAsc}
                onChange={handleSortLinkChange}
            >
                <FormattedMessage
                    id="price_list.quotes.listing.headers.proposed_at_label"
                    defaultMessage="Order date"
                />
            </SortLink>,
            <SortLink
                key={4}
                field={"totalAmount"}
                sortField={searchFilters.sortField}
                asc={searchFilters.sortAsc}
                onChange={handleSortLinkChange}
            >
                <FormattedMessage
                    id="price_list.quotes.listing.headers.amount_label"
                    defaultMessage="Amount"
                />
            </SortLink>,
            <SortLink
                key={5}
                field={"status"}
                sortField={searchFilters.sortField}
                asc={searchFilters.sortAsc}
                onChange={handleSortLinkChange}
            >
                <FormattedMessage
                    id="price_list.quotes.listing.headers.status_label"
                    defaultMessage="Status"
                />
            </SortLink>,
        ]

        if (props.priceListObjectType === "invoice") {
            headers.push(<FormattedMessage
                id="price_list.quotes.listing.headers.returns_label"
                defaultMessage="Returns"
            />)
        }

        return headers
    }

    const row = (object) => {
        const hasAssociatedReturn = object.refunded && object.relatedReturns && object.relatedReturns.length > 0
        const returnLinks = [] as JSX.Element[]
        const contact = [] as JSX.Element[]
        if (hasAssociatedReturn) {
            for (const returnInfo of object.relatedReturns) {
                returnLinks.push(
                    <Link key={returnInfo.Id} to={"/returns/" + returnInfo.Id + "/edit"}>
                        {returnInfo.Identifier}
                    </Link>)
            }
        }

        const contactName = object.contact ? object.contact.name : ""
        const customer = object.contact || {}
        contact.push(<Link key={contact.length} to={"/contacts/" + object.contactId + "/details"}>
            {contactName}
        </Link>)

        if (customer.organizationId && customer.type === "persons") {
            contact.push(<Link className={"ml-1"} key={contact.length} to={"/contacts/" + customer.organizationId + "/details"}>
                ({customer.organizationName})
            </Link>)
        }

        const row = [
            getIDLinkByObjectType(object),
            contact,
            object.effectiveDate ? <FormattedDate
                year='numeric'
                month='long'
                day='numeric'
                value={new Date(object.effectiveDate)} /> : "-",
            getAmountToDisplayByObjectType(object),
            getStatusToDisplayByObjectType(object),
        ]

        if (props.priceListObjectType === "invoice") {
            row.push(returnLinks.length > 0 ? returnLinks.map((item, index) => (
                <span key={item.key}>
                    {item}
                    {index < returnLinks.length - 1 && ", "}
                </span>
            )) : "-")
        }
        return row
    }

    const openExportModal = (e) => {
        e.preventDefault()
        setExportModal(
            <InvoiceExportModal
                filters={data}
                onClose={() => setExportModal(<></>)}
            />
        )
    }

    const getIDLinkByObjectType = (object) => {
        switch (object._type) {
        case "invoices":
        case "quotes":
            return <Link to={`/${object._type}/${object.id}/edit`}>
                {object.identifier}
            </Link>
        case "credit-memos":
            return <CreditMemoModalLink
                creditMemoId={object.id}
                creditMemoIdentifier={object.identifier}
                contactId={object.contactId} />
        }

        if (object._index?.includes("invoices")) {
            return <Link to={`/invoices/${object.id}/edit`}>
                {object.identifier}
            </Link>
        }

        if (object._index?.includes("credit-memos")) {
            return <CreditMemoModalLink
                creditMemoId={object.id}
                creditMemoIdentifier={object.identifier}
                contactId={object.contactId} />
        }

        if (object._index?.includes("quotes")) {
            return <Link to={`/quotes/${object.id}/edit`}>
                {object.identifier}
            </Link>
        }

        return "-"
    }

    const getAmountToDisplayByObjectType = (object) => {
        switch (object._type) {
        case "invoices":
        case "quotes":
            return <FormattedNumber
                style="currency"
                currency={object.currency || AppContext.getAccount().attributes.currency}
                value={object.totalAmount}
            />
        case "credit-memos":
            return <FormattedNumber
                style="currency"
                currency={object.currency || AppContext.getAccount().attributes.currency}
                value={object.amount}
            />
        default: return "-"
        }
    }

    const getStatusToDisplayByObjectType = (object) => {
        switch (object._type) {
        case "invoices":
            return <InvoiceStatusBadge
                status={object.status}
            />
        case "quotes":
            return <QuoteStatusBadge
                status={object.status}
            />
        case "credit-memos":
            return <CreditMemoStatusBadge
                status={object.status}
            />
        default: return "-"
        }
    }

    const handlePageSizeChange = (size) => {
        setSearchFilters({
            ...searchFilters,
            activePage: 1,
            pageSize: size
        })
    }

    const handlePageChange = (pageNumber) => {
        setSearchFilters({
            ...searchFilters,
            activePage: pageNumber
        })
    }

    const handleSortLinkChange = (sortField, asc) => {
        setSearchFilters({
            ...searchFilters,
            sortField: sortField,
            sortAsc: asc,
        })
    }

    if (loading) {
        return <LoadingSpinner />
    }

    if (searchResult.rows.length === 0) {
        return <NoResult
            className={"py-2"}
            title={<FormattedMessage
                id="price_list.analytics.no_order_yet_title"
                defaultMessage="No order received yet."
            />}
            message={<FormattedMessage
                id="price_list.analytics.no_order_yet_message"
                defaultMessage="No order has been received yet. Time to share the link!"
            />}
            noButton={true}
        />
    }

    return <>
        <StandardList
            smallLine={true}
            className="row pt-2 d-none d-sm-none d-md-block"
            headers={searchResult.headers}
            rows={searchResult.rows}
        />
        <Pagination
            showPageSize={true}
            onPageSizeChange={handlePageSizeChange}
            activePage={searchFilters.activePage}
            pageSize={searchFilters.pageSize}
            totalItemsCount={searchResult.totalItemsCount}
            onChange={handlePageChange}
        >
            {props.priceListObjectType == "invoice" &&
                <div className="ml-2">
                    <button
                        onClick={openExportModal}
                        className="ml-2 btn btn-sm btn-secondary">
                        <Icon value={"file-export"} className={"mr-1"} />
                        <FormattedMessage
                            id="listing.search.export"
                            defaultMessage="Export all results"
                        />
                    </button>
                </div>}
        </Pagination>
        {exportModal}
    </>
}

export default PriceListOrderListingComponent


// WEBPACK FOOTER //
// ./src/price-list/PriceListOrderListingComponent.tsx